import type { AxiosResponse } from 'axios';

import api from '@/utils/axiosInterceptor';

export interface IItemList {
  ID: number;
  Name: string;
  Price: number;
}

export interface IItemEyeGroup {
  clientID: number;
  clientOID: string;
  clientName: string;
  allItems: number;
  notLinkedItems: number;
  ignoreItems: number;
  clientClass: string;
  purchasesMan: {
    _id: string;
    empName: string;
    avatar: string;
    job: {
      _id: string;
      jobName: string;
    };
  };
}

export interface IMainPageData {
  suppliars: IItemEyeGroup[];
  allSuppliars: number;
  allSuppliarsA: number;
  allSuppliarsB: number;
  allSuppliarsC: number;
  allSuppliarsD: number;
  allLinks: number;
  allLinksIgnore: number;
  allLinksWithConnect: number;
  allLinksWithOutConnect: number;
  purchasesMenData: {
    a: number;
    allClients: number;
    allItems: number;
    b: number;
    c: number;
    d: number;
    ignoreItems: number;
    linkWithEmp: boolean;
    notLinkedItems: number;
    purchasesMan: {
      _id: string;
      empName: string;
      avatar: string;
      job: {
        _id: string;
        jobName: string;
      };
    };
  }[];
}

export interface ISupplierItem {
  _id: string;
  __v: number;
  LinkedDate: string;
  ClientID: string;
  UserID: {
    _id: string;
    empName: string;
    avatar: string;
    job: {
      _id: string;
      jobName: string;
    };
  };
  ClientItemID: number | null;
  ClientItemName: string;
  HealthyItemID: number;
  IsIgnoreItem: boolean;
  oldUserName: string;
  Price: number;
  healthyDataName: string;
  healthyDataID: number;
  healthyDataPrice: number;
  healthyDataIsEnabled: boolean;
  isPriceDiff: boolean;
}

export interface ISummaryData {
  countHeads: number;
  countsBodies: number;
  last: {
    _id: string;
    date: string;
    user: { _id: string; avatar: string; empName: string } | null;
  };
}

export interface ISupplierData {
  _id: string;
  ID: number;
  coordinates: number[]; // Adjust the type as needed
  isSuppliar: boolean;
  suppliarClass: string;
  paperImgs: string[]; // Adjust the type as needed
  createdAt: string;
  updatedAt: string;
  purchasesMan: {
    _id: string;
    empName: string;
    avatar: string;
    job: {
      _id: string;
      jobName: string;
    };
  };
  __v: number;
  Code: string;
  Name: string;
  Address: string;
  CityID: number;
  CityName: string;
  StateID: number;
  StateName: string;
  Tel: string;
  Tel2: string;
  Fax: string;
  Email: string;
  Mobile: string;
  Notes: string;
  Enabled: boolean;
  ClientSupplierID: number;
  trustLimit: number;
  RegionID: number;
  RegionName: string;
  SpecialDiscount: number;
  BulkDiscount: number;
  NormalDiscount: number;
  ClassID: number;
  ClassName: string;
  ContactTel3: string;
  IsBlackListSales: boolean;
  Zip: string;
  TypeID: number;
  TypeName: string;
  ClientSupplierName: string;
  ContactName: string;
  HasDeal: boolean;
  PaymentPeriod: number;
  PaymentPeriodType: string;
  PaymentType: string;
  Debt: number;
  allItems: number;
  linkedItems: number;
  ignoreItems: number;
  unLinkedItems: number;
  itemsWithPriceDiff: number;
  itemsList: ISupplierItem[];
  OffersData: ISummaryData;
  RequestsData: ISummaryData;
}

export async function GetItemsEyesData() {
  try {
    const response: AxiosResponse<IMainPageData> =
      await api.get(`/itemeyes/links-main`);
    return response?.data;
  } catch (error) {
  }
}

export async function GetOneSuppliar(id: string) {
  try {
    const response: AxiosResponse<ISupplierData> = await api.get(
      `/itemeyes/suppliar/${id}`,
    );
    return response?.data;
  } catch (error) {
  }
}

// Centralized cache for all items to prevent multiple API calls
let itemsCache: IItemList[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export async function GetAllItems() {
  const now = Date.now();

  // Return cached data if it's still valid
  if (itemsCache && now - cacheTimestamp < CACHE_DURATION) {
    return itemsCache;
  }

  try {
    const response: AxiosResponse<IItemList[]> =
      await api.get(`/itemeyes/all-items`);
    itemsCache = response?.data || [];
    cacheTimestamp = now;
    return itemsCache;
  } catch (error) {
    // Return cached data even if expired, if available
    if (itemsCache) {
      return itemsCache;
    }
    return [];
  }
}

// Function to clear the cache (useful for testing or when data needs refresh)
export function clearItemsCache() {
  itemsCache = null;
  cacheTimestamp = 0;
}

// Function to get cached items without API call
export function getCachedItems(): IItemList[] | null {
  const now = Date.now();
  if (itemsCache && now - cacheTimestamp < CACHE_DURATION) {
    return itemsCache;
  }
  return null;
}

// Simplified search function that works reliably
export async function SearchItems(searchTerm: string, limit: number = 50) {
  try {
    // First, try to call the search API if it exists
    const response: AxiosResponse<IItemList[]> = await api.get(
      `/itemeyes/search-items`,
      {
        params: {
          search: searchTerm,
          limit,
        },
      },
    );
    return response?.data;
  } catch (error) {
    // If search API doesn't exist, use simple client-side filtering

    try {
      // Get all items (this is cached by React Query)
      const allItems = await GetAllItems();
      if (!allItems || allItems.length === 0) return [];

      // Simple and efficient filtering
      const filteredItems = allItems
        .filter((item) => {
          const nameMatch = item.Name.toLowerCase().includes(
            searchTerm.toLowerCase(),
          );
          const idMatch = item.ID.toString().includes(searchTerm);
          return nameMatch || idMatch;
        })
        .slice(0, limit);

      return filteredItems;
    } catch (fallbackError) {
      return [];
    }
  }
}

export async function UpdateIgnore(id: string) {
  try {
    const response: AxiosResponse<ISupplierData> = await api.patch(
      `/itemeyes/update-link-ignore/${id}`,
    );
    return response?.data;
  } catch (error) {
  }
}

export async function AddManyLinks({
  id,
  items,
}: {
  id: string;
  items: {
    itemOfferName: string;
    itemOfferPrice: number;
    itemOfferID: number;
    itemHealthyID: number;
  }[];
}) {
  try {
    const response: AxiosResponse<ISupplierData> = await api.patch(
      `/itemeyes/add-many-links`,
      { id, items },
    );
    return response?.data;
  } catch (error) {
  }
}

export async function AddManyIgnore({
  id,
  items,
}: {
  id: string;
  items: {
    itemOfferName: string;
    itemOfferPrice: number;
    itemOfferID: number;
  }[];
}) {
  try {
    const response: AxiosResponse<ISupplierData> = await api.patch(
      `/itemeyes/add-many-ignore`,
      { id, items },
    );
    return response?.data;
  } catch (error) {
  }
}

export async function DeleteLink(id: string) {
  try {
    const response = await api.delete(`/itemeyes/delete-link/${id}`);
    return response?.data;
  } catch (error) {
  }
}

export async function UpdateItemLink({
  id,
  item,
}: {
  id: string;
  item: number;
}) {
  // eslint-disable-next-line no-useless-catch
  try {
    const response: AxiosResponse<ISupplierData> = await api.patch(
      `/itemeyes/update-link-item`,
      { id, item },
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function UpdateItemLinkForce({
  id,
  item,
}: {
  id: string;
  item: number;
}) {
  // eslint-disable-next-line no-useless-catch
  try {
    const response: AxiosResponse<ISupplierData> = await api.patch(
      `/itemeyes/update-link-item-f`,
      { id, item },
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function UpdateItemLinkWithGroup({
  id,
  newPrice,
}: {
  id: string;
  newPrice: number;
}) {
  // eslint-disable-next-line no-useless-catch
  try {
    const response: AxiosResponse<ISupplierData> = await api.patch(
      `/itemeyes/update-link-item-group`,
      { id, newPrice },
    );
    return response?.data;
  } catch (err) {
    throw err;
  }
}

export async function DeleteManyLinks({ ids }: { ids: string[] }) {
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await api.patch(`/itemeyes/delete-many`, { ids });
    return response?.data;
  } catch (err) {
    throw err;
  }
}
